import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { GetPatientsQuery, Patient } from "@models/patient";
import { PatientSettings } from "@models/patient-settings";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { ListResponse } from "@models/list-response";
import buildFormData from "@utils/buildFormData";
import { personApi } from "@store/personApi";
import { accountSetupApi } from "@store/accountSetupApi";
import { characterApi } from "@store/characterApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export const patientApi = createApi({
  reducerPath: "patientApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/patients`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["Patient", "FamilyMember", "PatientSettings"],
  endpoints: (builder) => ({
    createPatient: builder.mutation<Patient, Partial<Patient>>({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: ({ avatar, ...rest }: Partial<Patient>) => {
        const formData = new FormData();
        buildFormData(formData, rest);
        if (avatar) {
          formData.append("avatar", avatar);
        }
        return {
          url: "",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(personApi.util.invalidateTags(["Person"]));
      },
    }),
    updatePatient: builder.mutation<Patient, Partial<Patient>>({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: ({ id, avatar, ...patient }: Partial<Patient>) => {
        const formData = new FormData();
        buildFormData(formData, patient);
        if (avatar) {
          formData.append("avatar", avatar);
        }

        return {
          url: id!,
          method: "PATCH",
          body: formData,
          formData: true,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(personApi.util.invalidateTags(["Person"]));
        dispatch(accountSetupApi.util.invalidateTags(["AccountSetupProgress"]));
        dispatch(characterApi.util.invalidateTags(["Character"]));
      },
    }),
    getPatients: builder.query<ListResponse<Patient>, GetPatientsQuery | void>({
      providesTags: ["Patient", "FamilyMember"],
      query: (params) => ({
        url: "",
        params: params
          ? {
              ...(params.includeArchived !== undefined && {
                includeArchived: params.includeArchived,
              }),
              ...(params.includeLastActivity !== undefined && {
                includeLastActivity: params.includeLastActivity,
              }),
            }
          : undefined,
      }),
    }),
    updatePatientStatus: builder.mutation<
      Patient,
      { id: string; isActive: boolean }
    >({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: ({ id, isActive }) => ({
        url: `${id}/status`,
        method: "PATCH",
        body: { isActive },
      }),
    }),
    archivePatient: builder.mutation<Patient, string>({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: (id) => ({
        url: `${id}/archive`,
        method: "PATCH",
      }),
    }),
    unarchivePatient: builder.mutation<Patient, string>({
      invalidatesTags: ["Patient", "FamilyMember"],
      query: (id) => ({
        url: `${id}/unarchive`,
        method: "PATCH",
      }),
    }),
    getPatient: builder.query<Patient, string>({
      providesTags: ["Patient", "FamilyMember"],
      query: (id: string) => id,
    }),
    getPatientSettings: builder.query<PatientSettings, string>({
      providesTags: (_result, _error, id) => [{ type: "PatientSettings", id }],
      query: (id: string) => `${id}/settings`,
    }),
    updatePatientSettings: builder.mutation<
      PatientSettings,
      { patientId: string } & Partial<PatientSettings>
    >({
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: "PatientSettings", id: patientId },
        "Patient",
      ],
      query: ({ patientId, ...body }) => ({
        url: `${patientId}/settings`,
        method: "PATCH",
        body,
      }),
    }),
  }),
});

export const {
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useGetPatientsQuery,
  useGetPatientQuery,
  useGetPatientSettingsQuery,
  useUpdatePatientSettingsMutation,
  useUpdatePatientStatusMutation,
  useArchivePatientMutation,
  useUnarchivePatientMutation,
} = patientApi;
